import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BookingModal from "../components/BookingModal";
import CategoryCards from "../components/CategoryCards";
import Gallery from "../components/Gallery";
import StructuredData from "../components/StructuredData";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Award,
  Wind,
  Users,
  Shield,
  GraduationCap,
  Sparkles,
  Star,
  MessageCircle,
  CheckCircle,
  ArrowRight,
  Calendar,
  MapPin,
  Globe,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const heroVideoRef = useRef(null);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;

    const tryPlay = () => {
      video.play().catch(() => {});
    };

    tryPlay();

    video.addEventListener("loadeddata", tryPlay);
    video.addEventListener("canplay", tryPlay);

    const onInteraction = () => {
      tryPlay();
      document.removeEventListener("touchstart", onInteraction);
      document.removeEventListener("scroll", onInteraction);
    };
    document.addEventListener("touchstart", onInteraction, { passive: true });
    document.addEventListener("scroll", onInteraction, { passive: true });

    return () => {
      video.removeEventListener("loadeddata", tryPlay);
      video.removeEventListener("canplay", tryPlay);
      document.removeEventListener("touchstart", onInteraction);
      document.removeEventListener("scroll", onInteraction);
    };
  }, []);


  // Real reviews from Google Business profile (Dakhla Kite Planet | IKO kitesurf school Dakhla - 5.0, 19 reviews)
  const GOOGLE_REVIEWS_URL =
    "https://www.google.com/maps/place/Dakhla+Kite+Planet+%7C+IKO+kitesurf+school+Dakhla/@22.6807513,-14.5526684,17z/data=!4m8!3m7!1s0x226aad8ce2284a5b:0xf48e0e64c6e3f0e2!8m2!3d22.6807513!4d-14.5526684!9m1!1b1!16s%2Fg%2F11trtk_jnl";

  const googleReviews = [
    {
      name: "Keara Pfeiff",
      time: { en: "2 weeks ago", fr: "il y a 2 semaines", es: "hace 2 semanas", de: "vor 2 Wochen" },
      text: "I had a really great experience taking lessons with Saysay at Dakhla Kite Planet! Saysay is a very experienced and knowledgeable instructor who would not only be great for beginners, but also kitesurfers looking to improve their riding.",
    },
    {
      name: "Corinne",
      time: { en: "3 years ago", fr: "il y a 3 ans", es: "hace 3 años", de: "vor 3 Jahren" },
      text: "It is such a pleasure and fun to learn kitesurfing with SaySay. The learning path is smooth and relax but still efficient as after a week we could all kite. He taught to myself and to my 3 kids, 16, 14 and 12 years old, adapting easily to each of us. I can strongly recommend SaySay for everyone who would like to enjoy kitesurfing.",
    },
    {
      name: "Med ben",
      time: { en: "1 week ago", fr: "il y a 1 semaine", es: "hace 1 semana", de: "vor 1 Woche" },
      text: "Hi Saysay, I just wanted to thank you so much for the fantastic kitesurfing course I took with you together with my daughter. You guided us with so much passion, inspiration, and patience, and your wonderful pedagogical approach helped enormously to overcome the nerves at the start. Thank you for everything!",
    },
    {
      name: "Guillaume Comte",
      time: { en: "1 month ago", fr: "il y a 1 mois", es: "hace 1 mes", de: "vor 1 Monat" },
      text: "A fantastic stay with top-notch, friendly, and highly competent professionals. Brahim and Saysay made us feel confident, and for a first-time kitesurfing experience, I was thrilled. I highly recommend it! An unforgettable stay.",
    },
    {
      name: "Louis Morren",
      time: { en: "2 years ago", fr: "il y a 2 ans", es: "hace 2 años", de: "vor 2 Jahren" },
      text: "Top school, excellent freestyle coaching with real and noticeable progress in just one week. Thanks to Say for his pro tips, good humor, and kindness. The atmosphere is fantastic, a great concept with great guys in a great location.",
    },
    {
      name: "Blair Trigger",
      time: { en: "2 years ago", fr: "il y a 2 ans", es: "hace 2 años", de: "vor 2 Jahren" },
      text: "Mansor and Say are legends! We had never kitesurfed and they were amazing teachers and now lifelong friends. Thanks legends!",
    },
  ];

  const googleNotice = {
    en: "Real reviews from our Google Business profile",
    fr: "Avis authentiques de notre fiche Google Business",
    es: "Reseñas reales de nuestro perfil de Google Business",
    de: "Echte Bewertungen von unserem Google Business-Profil",
  };

  const googleSeeAll = {
    en: "See all 19 reviews on Google",
    fr: "Voir les 19 avis sur Google",
    es: "Ver las 19 reseñas en Google",
    de: "Alle 19 Bewertungen auf Google ansehen",
  };

  const GoogleLogo = ({ className = "h-6 w-6" }) => (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );

  const features = [
    {
      icon: GraduationCap,
      title: t.features.certification,
      description: t.features.certificationDesc,
      isList: true,
      listItems: t.features.certificationList,
    },
  ];

  const images = {
    hero: "https://images.unsplash.com/photo-1627068477565-3a66d5f76d5e",
    action1: "https://images.unsplash.com/photo-1690681200064-0302728b3572",
    action2: "https://images.unsplash.com/photo-1578060124065-41f863eb9ebe",
    lagoon: "https://images.unsplash.com/photo-1578129881872-c13ed0add1de",
    desert: "https://images.unsplash.com/photo-1542401886-65d6c61db217",
    equipment: "https://images.unsplash.com/photo-1762549957454-d5551dfe2f44",
    beach: "https://images.unsplash.com/photo-1661426795053-08140bc5c688",
    instructor: "https://images.unsplash.com/photo-1564499504739-bc4fc2ae8cba",
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, {
        ...contactForm,
        language: language,
      });

      toast.success(t.contact.success);
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <StructuredData language={language} />
      <Header onBookingClick={() => setBookingModalOpen(true)} />

      {/* Hero Section with Video Background */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: "url(https://customer-assets.emergentagent.com/job_53c07cd7-1ab5-4cff-8752-32dcf9dc74c8/artifacts/vzwdz346_IMG_3881.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <video
            ref={heroVideoRef}
            src={isMobile ? "/hero-video-mobile.mp4" : "/hero-video.mp4"}
            autoPlay
            muted
            loop
            playsInline
            webkit-playsinline=""
            preload="metadata"
            poster="https://customer-assets.emergentagent.com/job_53c07cd7-1ab5-4cff-8752-32dcf9dc74c8/artifacts/vzwdz346_IMG_3881.jpeg"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-6 bg-yellow-500/90 text-gray-900 hover:bg-yellow-400 text-sm px-4 py-2">
            {t.about.founded} • IKO Certified
          </Badge>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            {t.hero.title}
          </h1>
          <p className="text-xl sm:text-2xl text-gray-200 mb-10 max-w-3xl mx-auto leading-relaxed">
            {t.hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setBookingModalOpen(true)}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-lg shadow-2xl transform hover:scale-105 transition-all"
            >
              {t.hero.cta}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={() => navigate("/packages")}
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-md border-white/30 text-white hover:bg-white/20 px-8 py-6 text-lg"
            >
              {t.hero.secondaryCta}
            </Button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section id="testimonials" className="py-24 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.testimonials.title}</h2>
            <div className="flex items-center justify-center gap-3 mb-3" data-testid="google-rating-badge">
              <GoogleLogo className="h-8 w-8" />
              <span className="text-3xl font-bold text-gray-900">5.0</span>
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <span className="text-gray-500 text-lg">(19)</span>
            </div>
            <p className="text-base text-gray-600 flex items-center justify-center gap-2" data-testid="google-reviews-notice">
              <svg className="h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1.5 15.5l-4-4 1.41-1.41 2.59 2.58 6.09-6.08L18 9l-7.5 7.5z" />
              </svg>
              {googleNotice[language]}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {googleReviews.map((review, index) => (
              <Card key={index} className="border-2 hover:border-blue-300 transition-all hover:shadow-xl" data-testid={`google-review-card-${index}`}>
                <CardContent className="p-8 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                      ))}
                    </div>
                    <GoogleLogo className="h-5 w-5" />
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic flex-1">
                    "{review.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{review.name}</p>
                      <p className="text-sm text-gray-500">{review.time[language]}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-3 bg-white border-2 border-gray-200 rounded-full font-semibold text-gray-800 hover:border-blue-400 hover:shadow-lg transition-all"
              data-testid="see-all-google-reviews-link"
            >
              <GoogleLogo className="h-5 w-5" />
              {googleSeeAll[language]}
            </a>
          </div>
        </div>
      </section>

      {/* Category Cards Section */}
      <CategoryCards />

      {/* Packages Section */}
      <section id="packages" className="py-24 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.packages.title}</h2>
            <p className="text-xl text-gray-600">{t.packages.subtitle}</p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto mt-4"></div>
          </div>

          {/* Accommodation Gallery */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Our Accommodations</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2 row-span-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/ddvw6rgn_IMG_4037.jpeg"
                  alt="Aerial view of Dakhla Kite Planet camp"
                  className="w-full h-full object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow"
                />
              </div>
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/ul9vir1m_IMG_4036.jpeg"
                  alt="Twin bedroom with traditional decor"
                  className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow"
                />
              </div>
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/4f4h4zgb_IMG_4035.jpeg"
                  alt="Room interior with view"
                  className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow"
                />
              </div>
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/nama5ift_IMG_4095.jpeg"
                  alt="Cozy twin bedroom with red pillows"
                  className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow"
                />
              </div>
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/xj5pjgh8_IMG_4094.jpeg"
                  alt="Double bedroom with Moroccan decor"
                  className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow"
                />
              </div>
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/n2wmn9n0_IMG_4090.jpeg"
                  alt="Traditional Moroccan room with rustic ceiling"
                  className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow"
                />
              </div>
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/5qwue95t_IMG_4092.jpeg"
                  alt="Traditional Moroccan dining with couscous"
                  className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow"
                />
              </div>
              <div className="col-span-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/gojcjjeg_IMG_4096.jpeg"
                  alt="Evening campfire with string lights"
                  className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Premium Kite Package */}
          <Card className="relative overflow-hidden border-2 hover:border-teal-400 transition-all hover:shadow-2xl transform hover:-translate-y-2 bg-gradient-to-br from-teal-50 to-white">
            <div className="absolute top-4 right-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              Best Value
            </div>
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.packages.premium.name}
              </h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-teal-600">
                  {t.packages.premium.price}
                </span>
                <span className="text-gray-600 ml-2 text-sm">{t.packages.premium.perPerson}</span>
                <p className="text-sm text-gray-500 mt-1">{t.packages.premium.duration}</p>
                <p className="text-xs text-gray-600 mt-2 italic">{t.packages.premium.description}</p>
              </div>
              <Badge className="mb-4 bg-teal-600 text-white">{t.packages.premium.badge}</Badge>
              <ul className="space-y-2 mb-8 max-h-60 overflow-y-auto">
                {t.packages.premium.highlights.map((highlight, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{highlight}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => setBookingModalOpen(true)}
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-semibold"
              >
                {t.packages.bookPackage}
              </Button>
            </CardContent>
          </Card>

          {/* Nomades Kite Package */}
          <Card className="relative overflow-hidden border-2 hover:border-teal-400 transition-all hover:shadow-2xl transform hover:-translate-y-2">
            <div className="absolute top-4 right-4 bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
              Popular
            </div>
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.packages.nomades.name}
              </h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-teal-600">
                  {t.packages.nomades.price}
                </span>
                <span className="text-gray-600 ml-2 text-sm">{t.packages.nomades.perPerson}</span>
                <p className="text-sm text-gray-500 mt-1">{t.packages.nomades.duration}</p>
                <p className="text-xs text-gray-600 mt-2 italic">{t.packages.nomades.description}</p>
              </div>
              <Badge className="mb-4 bg-teal-600 text-white">{t.packages.nomades.badge}</Badge>
              <ul className="space-y-2 mb-8 max-h-60 overflow-y-auto">
                {t.packages.nomades.highlights.map((highlight, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{highlight}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => setBookingModalOpen(true)}
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              >
                {t.packages.bookPackage}
              </Button>
            </CardContent>
          </Card>

          {/* Kite Rental Package */}
          <Card className="relative overflow-hidden border-2 hover:border-teal-400 transition-all hover:shadow-2xl transform hover:-translate-y-2">
            <div className="absolute top-4 right-4 bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Flexible
            </div>
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.packages.rental.name}
              </h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-teal-600">
                  {t.packages.rental.price}
                </span>
                <span className="text-gray-600 ml-2 text-sm">{t.packages.rental.perPerson}</span>
                <p className="text-sm text-gray-500 mt-1">{t.packages.rental.duration}</p>
                <p className="text-xs text-gray-600 mt-2 italic">{t.packages.rental.description}</p>
              </div>
              <Badge className="mb-4 bg-teal-600 text-white">{t.packages.rental.badge}</Badge>
              <ul className="space-y-2 mb-8 max-h-60 overflow-y-auto">
                {t.packages.rental.highlights.map((highlight, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{highlight}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => setBookingModalOpen(true)}
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              >
                {t.packages.bookPackage}
              </Button>
            </CardContent>
          </Card>

          {/* Surf Lesson Package */}
          <Card className="relative overflow-hidden border-2 hover:border-teal-400 transition-all hover:shadow-2xl transform hover:-translate-y-2">
            <div className="absolute top-4 right-4 bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
              New
            </div>
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.packages.surfLesson.name}
              </h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-teal-600">
                  {t.packages.surfLesson.price}
                </span>
                <span className="text-gray-600 ml-2 text-sm">{t.packages.surfLesson.perPerson}</span>
                <p className="text-sm text-gray-500 mt-1">{t.packages.surfLesson.duration}</p>
                <p className="text-xs text-gray-600 mt-2 italic">{t.packages.surfLesson.description}</p>
              </div>
              <Badge className="mb-4 bg-teal-600 text-white">{t.packages.surfLesson.badge}</Badge>
              <ul className="space-y-2 mb-8 max-h-60 overflow-y-auto">
                {t.packages.surfLesson.highlights.map((highlight, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{highlight}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => setBookingModalOpen(true)}
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              >
                {t.packages.bookPackage}
              </Button>
            </CardContent>
          </Card>

          {/* Surf Guiding Package */}
          <Card className="relative overflow-hidden border-2 hover:border-teal-400 transition-all hover:shadow-2xl transform hover:-translate-y-2">
            <div className="absolute top-4 right-4 bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
              New
            </div>
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.packages.surfGuiding.name}
              </h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-teal-600">
                  {t.packages.surfGuiding.price}
                </span>
                <span className="text-gray-600 ml-2 text-sm">{t.packages.surfGuiding.perPerson}</span>
                <p className="text-sm text-gray-500 mt-1">{t.packages.surfGuiding.duration}</p>
                <p className="text-xs text-gray-600 mt-2 italic">{t.packages.surfGuiding.description}</p>
              </div>
              <Badge className="mb-4 bg-teal-600 text-white">{t.packages.surfGuiding.badge}</Badge>
              <ul className="space-y-2 mb-8 max-h-60 overflow-y-auto">
                {t.packages.surfGuiding.highlights.map((highlight, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{highlight}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => setBookingModalOpen(true)}
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              >
                {t.packages.bookPackage}
              </Button>
            </CardContent>
          </Card>
        </div>
        </div>
      </section>

      {/* Gallery Section */}
      <Gallery />

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-700 text-sm px-3 py-1">
                {t.about.founded}
              </Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">{t.about.title}</h2>
              <p className="text-gray-700 leading-relaxed mb-6">{t.about.description}</p>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200">
                  <h4 className="font-bold text-gray-900 mb-2">{t.about.instructors}</h4>
                  <p className="text-sm text-gray-600">{t.about.instructorsDesc}</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-200 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/3p2szwm9_Screenshot_20230215-030246.jpeg" 
                    alt="IKO Affiliated Center - International Kiteboarding Organization" 
                    className="max-h-32 w-auto object-contain"
                  />
                </div>
              </div>

              <Button
                onClick={() => setBookingModalOpen(true)}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white"
              >
                <Calendar className="mr-2 h-5 w-5" />
                {t.nav.bookNow}
              </Button>
            </div>

            <div className="space-y-6">
              {/* Founder Section */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                  Meet the Founder & Head Instructor
                </h3>
                <div className="flex flex-col md:flex-row gap-6 items-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/xq5x7g90_Screenshot_20240205-203402.jpeg"
                    alt="SAYSAY - Founder and Head Instructor"
                    className="w-48 h-48 rounded-full object-cover shadow-lg border-4 border-white"
                  />
                  <div className="text-center md:text-left">
                    <h4 className="text-2xl font-bold text-blue-600 mb-1">SAYSAY</h4>
                    <Badge className="mb-3 bg-yellow-500 text-gray-900 font-semibold">
                      IKO COACH FREESTYLE
                    </Badge>
                    <p className="text-gray-700 leading-relaxed italic">
                      "Water sports oriented and a lifestyle dedicated to the ocean, since 20 years I decided to be fully engaged to teach and share my passion for kiteboarding."
                    </p>
                    <p className="text-blue-600 font-semibold mt-3">Welcome to Dakhla!</p>
                  </div>
                </div>
              </div>
              
              {/* Action Shot */}
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/g7m0uaec_say.jpeg"
                  alt="SAYSAY performing freestyle kiteboarding"
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Why Choose Dakhla Kite Planet */}
      <section className="py-24 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.features.title}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className={`group hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-blue-300 bg-white ${feature.isList ? 'md:col-span-2 lg:col-span-3' : ''}`}
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <feature.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  {feature.isList && feature.listItems ? (
                    <ul className="space-y-3">
                      {feature.listItems.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.faq.title}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto"></div>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white border-2 border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                {t.faq.q1}
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                {t.faq.a1}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white border-2 border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                {t.faq.q2}
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                {t.faq.a2}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white border-2 border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                {t.faq.q3}
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                {t.faq.a3}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white border-2 border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                {t.faq.q4}
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                {t.faq.a4}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white border-2 border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                {t.faq.q5}
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                {t.faq.a5}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="bg-white border-2 border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                {t.faq.q6}
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                {t.faq.a6}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="bg-white border-2 border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                {t.faq.q7}
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 leading-relaxed">
                {t.faq.a7}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.contact.title}</h2>
            <p className="text-xl text-gray-600">{t.contact.subtitle}</p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto mt-4"></div>
          </div>

          <Card className="border-2 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleContactSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name">{t.contact.name} *</Label>
                    <Input
                      id="contact-name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">{t.contact.email} *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-phone">{t.contact.phone}</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    data-testid="contact-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-message">{t.contact.message} *</Label>
                  <Textarea
                    id="contact-message"
                    rows={5}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white py-6 text-lg"
                  data-testid="contact-submit-btn"
                >
                  {loading ? "..." : t.contact.send}
                </Button>
              </form>
              <a
                href="https://wa.me/212667240623"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 w-full flex items-center justify-center gap-2 py-3 border-2 border-green-500 text-green-600 rounded-lg font-semibold hover:bg-green-50 transition-colors"
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle className="h-5 w-5" />
                {t.contact.whatsapp}
              </a>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
      
      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/212667240623"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center group"
        aria-label="Chat on WhatsApp"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="currentColor" 
          className="w-7 h-7"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="absolute right-full mr-3 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Chat with us!
        </span>
      </a>
      
      <BookingModal 
        isOpen={bookingModalOpen} 
        onClose={() => setBookingModalOpen(false)}
      />
    </div>
  );
};

export default HomePage;
